/**
 * Rich text editor (Word-like) for styling and formatting content.
 * Uses TipTap (React 19 compatible); value/onChange use HTML strings for API compatibility.
 */
import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatStrikethrough as StrikeIcon,
  FormatListNumbered as OrderedListIcon,
  FormatListBulleted as BulletListIcon,
  Title as TitleIcon,
  FormatColorText as TextColorIcon,
  Highlight as HighlightIcon,
} from '@mui/icons-material';

const TEXT_COLORS = [
  { label: 'Black', value: '#000000' },
  { label: 'Red', value: '#e53935' },
  { label: 'Blue', value: '#1e88e5' },
  { label: 'Green', value: '#43a047' },
  { label: 'Orange', value: '#fb8c00' },
  { label: 'Purple', value: '#8e24aa' },
];

const BG_COLORS = [
  { label: 'Yellow', value: '#ffff00' },
  { label: 'Light yellow', value: '#ffeb3b' },
  { label: 'Light green', value: '#c8e6c9' },
  { label: 'Light blue', value: '#bbdefb' },
  { label: 'Light orange', value: '#ffccbc' },
  { label: 'Light gray', value: '#f5f5f5' },
];

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  label?: string;
  minHeight?: number;
  error?: boolean;
  helperText?: string;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder: placeholderProp,
  label,
  minHeight = 280,
  error,
  helperText,
}: RichTextEditorProps) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp ?? t('components.richText.placeholder');
  const isInternalUpdate = useRef(false);
  const [textColorAnchor, setTextColorAnchor] = useState<HTMLElement | null>(null);
  const [bgColorAnchor, setBgColorAnchor] = useState<HTMLElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        style: `min-height: ${minHeight}px; outline: none;`,
      },
    },
    onUpdate: ({ editor }) => {
      if (isInternalUpdate.current) return;
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value (e.g. from API) into editor
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalized = value || '<p></p>';
    if (current !== normalized && !isInternalUpdate.current) {
      isInternalUpdate.current = true;
      editor.commands.setContent(normalized, false);
      isInternalUpdate.current = false;
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ mb: 1 }}
          color={error ? 'error.main' : 'text.primary'}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <ToggleButtonGroup
          size="small"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0,
            p: 0.5,
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiToggleButtonGroup-grouped': { border: 0 },
          }}
        >
          <ToggleButton
            value="bold"
            selected={editor.isActive('bold')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive('italic')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="strike"
            selected={editor.isActive('strike')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <StrikeIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="h2"
            selected={editor.isActive('heading', { level: 2 })}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <TitleIcon fontSize="small" /> H2
          </ToggleButton>
          <ToggleButton
            value="h3"
            selected={editor.isActive('heading', { level: 3 })}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </ToggleButton>
          <ToggleButton
            value="bulletList"
            selected={editor.isActive('bulletList')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <BulletListIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="orderedList"
            selected={editor.isActive('orderedList')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <OrderedListIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="textColor"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setTextColorAnchor(e.currentTarget)}
            sx={{ borderLeft: '1px solid', borderColor: 'divider' }}
          >
            <TextColorIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton
            value="bgColor"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setBgColorAnchor(e.currentTarget)}
          >
            <HighlightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Popover
          open={!!textColorAnchor}
          anchorEl={textColorAnchor}
          onClose={() => setTextColorAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
            {TEXT_COLORS.map(({ value }) => (
              <Box
                key={value}
                onClick={() => {
                  editor.chain().focus().setColor(value).run();
                  setTextColorAnchor(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: value,
                  border: '2px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                title={value}
              />
            ))}
            <ToggleButton
              size="small"
              value="remove"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setTextColorAnchor(null);
              }}
              sx={{ ml: 0.5 }}
            >
              {t('components.richText.remove')}
            </ToggleButton>
          </Box>
        </Popover>
        <Popover
          open={!!bgColorAnchor}
          anchorEl={bgColorAnchor}
          onClose={() => setBgColorAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
            {BG_COLORS.map(({ value }) => (
              <Box
                key={value}
                onClick={() => {
                  editor.chain().focus().setHighlight({ color: value }).run();
                  setBgColorAnchor(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: value,
                  border: '2px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                title={value}
              />
            ))}
            <ToggleButton
              size="small"
              value="remove"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                editor.chain().focus().unsetHighlight().run();
                setBgColorAnchor(null);
              }}
              sx={{ ml: 0.5 }}
            >
              {t('components.richText.remove')}
            </ToggleButton>
          </Box>
        </Popover>
        <Box
          sx={{
            '& .ProseMirror': {
              minHeight,
              px: 2,
              py: 1.5,
              '& p.is-editor-empty:first-child::before': {
                content: `"${placeholder}"`,
                color: 'text.disabled',
                float: 'left',
                height: 0,
                pointerEvents: 'none',
              },
              '& h1': { fontSize: '1.75rem', fontWeight: 700 },
              '& h2': { fontSize: '1.5rem', fontWeight: 600 },
              '& h3': { fontSize: '1.25rem', fontWeight: 600 },
              '& ul, & ol': { pl: 3 },
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error.main' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;
